import {
  Box,
  IconButton,
  Tooltip,
  useTheme,
  withStyles
} from '@material-ui/core';
import { SETVIImage, SHighlightText, SText } from '@setvi/shared/components';
import { TextColor } from '@setvi/shared/enums';
import { Customer, Product as ProductDTO } from '@setvi/shared/interfaces';
import { useTableStyles } from 'Components/Shared';
import {
  getProductStatus,
  ProductLabel,
  ProductStatusBadge
} from 'componentsV2';
import { Stock } from 'pages/products/components/stock';
import { toTitleCase } from '@setvi/shared/utils/formatCustomerData';
import { RemoveItemIcon } from 'pages/quotes/icons/filled-icons';
import { getStockInfo } from 'pages/products/components/products-list/useProductColumns/contents/stock-info';

type ProductWithTempId = ProductDTO & { tempId: string };

const ImagePreview = withStyles({
  tooltip: {
    padding: '2px !important',
    borderRadius: 6
  }
})(Tooltip);

export const useSelectedProductsColumns = (
  handleRemoveProduct: (tempId: string) => void,
  handleUpdateProduct: (
    product: ProductWithTempId,
    newValues: Partial<ProductDTO>
  ) => void,
  selectedCustomer?: Customer,
  showStock = true
) => {
  const { palette } = useTheme();
  const classes = useTableStyles();

  const columns = [
    {
      id: 'productName',
      minWidth: '300px',
      label: 'Product Name',
      cellContent: (product: ProductDTO) => {
        const productSrc =
          product?.imageUrl ||
          product?.images?.[0]?.path ||
          (product?.images?.[0] as unknown as string) ||
          '/images/product-placeholder.svg';
        return (
          <Box display="flex" alignItems="center">
            <ImagePreview
              interactive
              title={
                <img
                  src={productSrc}
                  alt="product-img"
                  onError={e => {
                    e.currentTarget.src = '/images/product-placeholder.svg';
                  }}
                  style={{
                    width: 200,
                    height: 200,
                    objectFit: 'contain',
                    backgroundColor: palette.common.white,
                    borderRadius: '4px',
                    padding: '4px'
                  }}
                />
              }
              placement="right"
              arrow>
              <Box>
                <SETVIImage
                  className={classes.tableImage}
                  src={productSrc}
                  alt={toTitleCase(product.name)}
                />
              </Box>
            </ImagePreview>
            <Box ml={4} display="flex" flexDirection="column">
              <Box display="flex" alignItems="center">
                <SHighlightText
                  value={toTitleCase(product.name)}
                  searchQuery=""
                  maxLines={5}
                />
              </Box>
              <Box display="flex" alignItems="center" gridGap="8px">
                <SHighlightText
                  style={{ color: TextColor.Grey }}
                  size="sm"
                  value={`SKU: ${product.sku}`}
                  searchQuery=""
                />
                <ProductStatusBadge
                  style={{
                    minWidth: 30,
                    width: 'fit-content'
                  }}>
                  <SText size="xs" title={getProductStatus(product)} />
                </ProductStatusBadge>
                {!!product?.stockInfo && (
                  <ProductStatusBadge>
                    <SText size="xs" title={getStockInfo(product?.stockInfo)} />
                  </ProductStatusBadge>
                )}
              </Box>
            </Box>
          </Box>
        );
      }
    },
    {
      id: 'manufacturer',
      minWidth: '200px',
      label: 'MFG Name & Sku',
      cellContent: (product: ProductDTO) => (
        <Box display="flex" flexDirection="column">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gridGap="4px">
            <SText title={product?.vendor?.name || '-'} />
            {product?.privateLabel && <ProductLabel />}
          </Box>
          <SText
            title={product?.manufacturerSku}
            size="sm"
            fontColor={TextColor.Grey}
          />
        </Box>
      )
    },
    ...(showStock
      ? [
          {
            id: 'stock',
            width: '150px',
            label: 'Stock',
            cellContent: (product: ProductWithTempId) => (
              <Box>
                <Stock
                  inventory={product?.inventory}
                  isDropship={product?.dropship}
                  isStockProduct={product?.isStockProduct}
                  selectedCustomer={selectedCustomer}
                  stockRefresh={{
                    id: product.sku,
                    onStockRefresh: (newInventory: any[]) =>
                      handleUpdateProduct(product, { inventory: newInventory })
                  }}
                />
              </Box>
            )
          }
        ]
      : []),
    {
      id: 'action',
      width: '50px',
      label: 'Action',
      cellContent: (product: ProductWithTempId) => (
        <IconButton
          onClick={e => {
            e.stopPropagation();
            handleRemoveProduct(product?.tempId);
          }}>
          <RemoveItemIcon />
        </IconButton>
      )
    }
  ];

  return { columns };
};
